import { Select, SimpleGrid } from '@mantine/core'
import { Controller, useWatch, type Control, type UseFormSetValue } from 'react-hook-form'
import { PRACTITIONER_QUALIFICATIONS, SPECIALTIES_BY_QUALIFICATION } from '@behuman/core-contract'

type Props = {
  // Shared by the Provider Profile tab and the invite modal, whose form value types differ
  // (see `LicensesField` for why `any` is the only control type both accept).

  control: Control<any>

  setValue: UseFormSetValue<any>
}

// The Specialty options a Professional Role carries: none for MD, DO and PA, for which the
// select is not rendered at all.
function specialtyOptionsFor(role: string | null | undefined): { value: string; label: string }[] {
  if (!role || !(role in SPECIALTIES_BY_QUALIFICATION)) return []
  return [...SPECIALTIES_BY_QUALIFICATION[role as keyof typeof SPECIALTIES_BY_QUALIFICATION]]
}

/**
 * The Professional Role and the Specialty on one row (see the glossary): the Profile tab's row
 * between the photo and the NPI, and the invite modal's row above the NPI. One of each per
 * Practitioner, shared by every state license. The Specialty select renders only for a role
 * that carries one (NP, RN) and is cleared whenever the role changes, so an MD never keeps a
 * nursing specialty.
 */
export function ProfessionalRoleFields({ control, setValue }: Props) {
  const role = useWatch({ control, name: 'professionalRole' }) as string | null | undefined
  const specialtyOptions = specialtyOptionsFor(role)

  return (
    <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
      <Controller
        name="professionalRole"
        control={control}
        render={({ field, fieldState }) => (
          <Select
            {...field}
            value={field.value ?? null}
            onChange={(value) => {
              field.onChange(value ?? null)
              setValue('specialty', null, { shouldDirty: true })
            }}
            label="Professional role"
            placeholder="Professional role"
            data={[...PRACTITIONER_QUALIFICATIONS]}
            searchable
            error={fieldState.error?.message}
          />
        )}
      />
      {specialtyOptions.length > 0 && (
        <Controller
          name="specialty"
          control={control}
          render={({ field, fieldState }) => (
            <Select
              {...field}
              value={field.value ?? null}
              onChange={(value) => field.onChange(value ?? null)}
              label="Specialty"
              placeholder="Specialty"
              data={specialtyOptions}
              clearable
              error={fieldState.error?.message}
            />
          )}
        />
      )}
    </SimpleGrid>
  )
}
